import { FileUp, FileX2, Phone, ClipboardCheck, UserPen, MessageSquareText, CalendarCheck, FileSignature, BadgeCheck } from 'lucide-react';

// Client activity in plain words (Nic 2026-10-23): one wording for the
// activity panel, the desk row and the history list, so all three agree.
// `labels` maps a checklist item to its title (the board's checklist), for
// "uploaded DD-214".

const listOf = (items) => {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

// The checklist items' titles (server/lib/onboardingChecklist.js), so a line
// reads the same with or without the board's labels.
const ITEM_TITLE = {
  signed_agreement: 'their signed agreement',
  itf: 'Intent to File',
  dd214: 'DD-214',
  rated: 'their disability list',
  private_records: 'private treatment records',
  service_records: 'their service treatment record',
  blue_button: 'their Blue Button report',
  decision_letters: 'VA decision letters',
};

const itemWord = (item, labels = {}) => (item && item !== 'other' ? ITEM_TITLE[item] || labels[item] || null : null);

const uploadLine = (m, labels) => {
  const files = m.files || [{ item: m.item, fileName: m.fileName }];
  if (files.length > 1) {
    const named = [...new Set(files.map((f) => itemWord(f.item, labels)).filter(Boolean))];
    return named.length ? `Uploaded ${files.length} files (${listOf(named)})` : `Uploaded ${files.length} files`;
  }
  const word = itemWord(files[0]?.item, labels);
  if (word) return `Uploaded ${word}`;
  return files[0]?.item === 'other' || !files[0]?.item ? 'Uploaded a file (something else: check what it is)' : 'Uploaded a file';
};

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

export const CLIENT_ACTIVITY_TEXT = {
  client_uploaded: (m, labels) => `${uploadLine(m, labels)}${m.replaced ? ' (replaced a file)' : ''}`,
  client_file_removed: (m) => `Removed a file${m.fileName ? `: ${m.fileName}` : ''}`,
  client_details_updated: (m) => `Updated their ${m.fields?.length ? listOf(m.fields) : 'details'}`,
  client_questionnaire_done: () => 'Finished the questionnaire',
  client_va_gov_done: () => 'Said VA.gov is set up',
  // Verify POA (Nic 2026-10-27): they tapped I've done it on the POA card.
  client_poa_done: () => 'Said their POA is removed',
  client_signed_correction: () => 'Signed their corrected agreement',
  client_texted: (m) => (m.preview ? `Texted: "${m.preview}"` : 'Texted us'),
  client_called: (m) => (m.answered === false ? 'Called, missed' : 'Called us'),
  client_booked: (m) => `Booked a meeting${m.startTime ? ` for ${when(m.startTime)}` : ''}`,
};

export const CLIENT_ACTIVITY_ICON = {
  client_uploaded: FileUp,
  client_file_removed: FileX2,
  client_details_updated: UserPen,
  client_questionnaire_done: ClipboardCheck,
  client_va_gov_done: BadgeCheck,
  client_poa_done: BadgeCheck,
  client_signed_correction: FileSignature,
  client_texted: MessageSquareText,
  client_called: Phone,
  client_booked: CalendarCheck,
};

// Where in their Workspace the change shows.
export const CLIENT_ACTIVITY_TAB = {
  client_uploaded: 'files',
  client_file_removed: 'files',
  client_va_gov_done: 'checklist',
  client_poa_done: 'checklist',
};

export const activityLine = (type, meta = {}, labels = {}) => (CLIENT_ACTIVITY_TEXT[type] || (() => 'Did something'))(meta || {}, labels);

/** "2m", "3h", "Tue", for the panel and rows. */
export const ago = (iso, now = new Date()) => {
  const s = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return new Date(iso).toLocaleDateString([], { weekday: 'short' });
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
};
